import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Ban, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

export default function Blocked() {
  const [items, setItems] = useState([]);
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("abuse");

  const load = async () => {
    try {
      const { data } = await api.get("/blocked");
      setItems(data.items || []);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!phone) return;
    try {
      await api.post("/blocked", { phone, reason });
      toast.success("Number blocked silently");
      setPhone("");
      setReason("abuse");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not block");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Unblock ${p}?`)) return;
    try {
      await api.delete(`/blocked/${encodeURIComponent(p)}`);
      toast.success("Unblocked");
      load();
    } catch {
      toast.error("Could not unblock");
    }
  };

  return (
    <div data-testid="blocked-page">
      <div className="mb-8">
        <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">
          Abuse Control
        </div>
        <h1 className="text-3xl font-medium tracking-tight text-white">Blocked Numbers</h1>
        <p className="text-sm text-white/50 mt-2">
          Silent block list. Submissions from these numbers receive a generic
          "we are unable to process your request" message — no error revealed,
          no call fires. Distinct from the DNC list (which is a compliance/regulatory list).
        </p>
      </div>

      <form onSubmit={add} data-testid="add-blocked-form" className="bg-[#121214] border border-white/10 rounded-xl p-5 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">
            Phone Number
          </label>
          <input
            data-testid="blocked-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+14155552671"
            className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 font-mono placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">
            Reason (internal)
          </label>
          <input
            data-testid="blocked-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
          />
        </div>
        <button
          data-testid="add-blocked-btn"
          type="submit"
          className="bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 font-medium text-sm px-4 py-2.5 rounded-md transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Block Silently
        </button>
      </form>

      <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
          <Ban className="w-4 h-4 text-red-400" />
          <div className="text-sm font-medium text-white">{items.length} blocked numbers</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="blocked-table">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                <th className="text-left px-5 py-3 font-normal">Phone</th>
                <th className="text-left px-5 py-3 font-normal">Reason</th>
                <th className="text-left px-5 py-3 font-normal">Added by</th>
                <th className="text-left px-5 py-3 font-normal">Added</th>
                <th className="text-right px-5 py-3 font-normal"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-white/40 text-sm">
                    No blocked numbers.
                  </td>
                </tr>
              )}
              {items.map((b) => (
                <tr key={b.phone} className="hover:bg-white/[0.02]">
                  <td className="px-5 py-3 font-mono text-red-300">{b.phone}</td>
                  <td className="px-5 py-3 text-white/60">{b.reason || "—"}</td>
                  <td className="px-5 py-3 text-white/50 font-mono text-xs">{b.added_by || "admin"}</td>
                  <td className="px-5 py-3 font-mono text-white/40 text-xs">
                    {b.added_at ? format(parseISO(b.added_at), "MMM d, yyyy · HH:mm") : "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      data-testid={`unblock-${b.phone}`}
                      onClick={() => remove(b.phone)}
                      className="text-white/40 hover:text-red-400 p-1"
                      title="Unblock"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
